
import React, { useContext } from 'react';
import { AppContext } from './contexts/AppContext';
import LoginScreen from './screens/LoginScreen';
import MemberLoginScreen from './screens/MemberLoginScreen';
import DashboardScreen from './screens/DashboardScreen';
import SociosScreen from './screens/SociosScreen';
import ProductosScreen from './screens/ProductosScreen';
import CajaScreen from './screens/CajaScreen';
import DeudasScreen from './screens/DeudasScreen';
import UsuariosScreen from './screens/UsuariosScreen';
import ReportesScreen from './screens/ReportesScreen';
import ReglasScreen from './screens/ReglasScreen';
import ConfiguracionScreen from './screens/ConfiguracionScreen';
import SuscripcionScreen from './screens/SuscripcionScreen';
import PortalSocioScreen from './screens/PortalSocioScreen';
import SuperAdminScreen from './screens/SuperAdminScreen';
import LandingScreen from './screens/LandingScreen';
import TurnosScreen from './screens/TurnosScreen'; // Importado
import ProfileScreen from './screens/ProfileScreen';
import Layout from './components/Layout';
import { Rol } from './types';
import OnboardingScreen from './screens/OnboardingScreen';
import ErrorBoundary from './components/ErrorBoundary';

const App: React.FC = () => {
  const { user, loading, error, activeScreen, unauthenticatedView, logout, clearError, club, fetchClubData } = useContext(AppContext);
  // Diagnostic logging to help identify if AppContext values are present
  try {
    console.log('App render', { userPresent: !!user, clubPresent: !!club, unauthenticatedView });
  } catch (e) {
    console.warn('App debug logging failed', e);
  }

  // --- 1. Manejo de estados globales (carga y error) ---
  if (error) {
    // Para errores específicos de "club no encontrado", ofrecemos cerrar sesión.
    if (error.includes('El club con ID')) {
      return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
          <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-lg text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">¡Ocurrió un Error!</h2>
            <p className="text-gray-700 mb-6">{error}</p>
            <button
              onClick={async () => await logout()}
              className="bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700"
            >
              Cerrar Sesión
            </button>
          </div>
        </div>
      );
    }
    
    // Para otros errores, mostramos el mensaje y permitimos cerrarlo sin recargar.
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-lg text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">¡Ocurrió un Error!</h2>
          <p className="text-gray-700 mb-6">{error}</p>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => clearError()}
              className="bg-gray-300 text-black py-2 px-6 rounded-md hover:bg-gray-400"
            >
              Cerrar
            </button>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700"
            >
              Intentar de Nuevo
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // --- 2. Lógica de enrutamiento según autenticación ---
  if (user) {
    // --- USUARIO AUTENTICADO ---
    // Si el usuario está autenticado pero no se cargó `club`, ofrecemos reintentar la carga.
    if (!club && !loading && !error) {
      return (
        <Layout>
          <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-lg text-center">
              <h2 className="text-xl font-bold mb-4">No se cargaron los datos del club</h2>
              <p className="text-gray-600 mb-6">Parece que la carga de datos falló. Podés intentar recargar los datos del club.</p>
              <div className="flex justify-center gap-4">
                <button onClick={() => fetchClubData(user.club_id as number)} className="bg-blue-600 text-white py-2 px-4 rounded-md">Reintentar carga</button>
                <button onClick={() => logout()} className="bg-gray-200 text-black py-2 px-4 rounded-md">Cerrar Sesión</button>
              </div>
            </div>
          </div>
        </Layout>
      );
    }
    // Wrap authenticated UI in an ErrorBoundary so render errors are visible and
    // provide a helpful stack trace instead of a blank screen.
    if (user.rol === Rol.SUPERADMIN) {
      return (
        <ErrorBoundary>
          <SuperAdminScreen />
        </ErrorBoundary>
      );
    }
    if (user.rol === Rol.SOCIO) {
      return (
        <ErrorBoundary>
          <PortalSocioScreen />
        </ErrorBoundary>
      );
    }

    const renderScreen = () => {
      switch (activeScreen) {
        case 'dashboard': return <DashboardScreen />;
        case 'perfil': return <ProfileScreen />;
        case 'socios': return <SociosScreen />;
        case 'productos': return <ProductosScreen />;
        case 'caja': return <CajaScreen />;
        case 'deudas': return <DeudasScreen />;
        case 'usuarios': return <UsuariosScreen />;
        case 'reportes': return <ReportesScreen />;
        case 'reglas': return <ReglasScreen />;
        case 'configuracion': return <ConfiguracionScreen />;
        case 'suscripcion': return <SuscripcionScreen />;
        case 'turnos': return <TurnosScreen />;
        default: return <DashboardScreen />;
      }
    };
    return (
      <ErrorBoundary>
        <Layout>{renderScreen()}</Layout>
      </ErrorBoundary>
    );

  } else {
    // --- USUARIO NO AUTENTICADO ---
    // Se usa un estado interno (`unauthenticatedView`) para cambiar entre las páginas
    // de bienvenida, login y registro, evitando recargas de página completas.
    if (unauthenticatedView === 'login') {
      return <LoginScreen />;
    }
    if (unauthenticatedView === 'login-socio') {
      return <MemberLoginScreen />;
    }
    if (unauthenticatedView === 'onboarding') {
      return <OnboardingScreen />;
    }
    return <LandingScreen />;
  }
};

export default App;
